/**
 * useConversationInfiniteScroll
 *
 * Responsabilidade: Gerenciar paginação por cursor e estado das conversas do chat.
 * Expõe API pública para carga inicial, scroll infinito, prepend e atualização pontual.
 *
 * Princípios: Single Responsibility, Composição (delega refresh ao useConversationRefresh)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { conversationDedupeKey } from '../../../services/repositories/helpers/ChatConversationMapper';
import { ChatConversation } from './types';
import { useConversationRefresh } from './useConversationRefresh';

interface UseConversationInfiniteScrollOptions {
    organizationId?: string;
    connectionId?: string;
    enabled?: boolean;
    pageSize?: number;
}

interface UseConversationInfiniteScrollReturn {
    conversations: ChatConversation[];
    setConversations: React.Dispatch<React.SetStateAction<ChatConversation[]>>;
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMoreConversations: boolean;
    loadInitial: () => Promise<void>;
    refresh: () => Promise<boolean>;
    loadMore: () => Promise<void>;
    prependConversation: (conv: ChatConversation) => void;
    updateConversation: (conv: ChatConversation) => void;
    reset: () => void;
}

const DEFAULT_PAGE_SIZE = 50;

export function useConversationInfiniteScroll({
    organizationId,
    connectionId,
    enabled = true,
    pageSize = DEFAULT_PAGE_SIZE,
}: UseConversationInfiniteScrollOptions): UseConversationInfiniteScrollReturn {
    const [conversations, setConversations] = useState<ChatConversation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMoreConversations, setHasMoreConversations] = useState(true);

    const cursorRef = useRef<string | undefined>(undefined);
    const isLoadingRef = useRef(false);
    const lastLoadTimeRef = useRef(0);
    const hasLoadedOnceRef = useRef(false);
    const previousOrgRef = useRef<string | undefined>(undefined);
    const previousConnRef = useRef<string | undefined>(undefined);

    const loadInitial = useCallback(async () => {
        if (!enabled || !organizationId || isLoadingRef.current) return;

        const currentOrgId = organizationId;
        isLoadingRef.current = true;
        setIsLoading(true);

        try {
            const result = await chatRepository.getConversationsPaginated(
                currentOrgId,
                undefined,
                pageSize,
                connectionId
            );

            if (currentOrgId !== previousOrgRef.current) return;

            // Carga inicial substitui — merge só no refresh (evita misturar canais após filtro)
            setConversations(result.conversations);
            setHasMoreConversations(result.hasMore);
            cursorRef.current = result.nextCursor;
            hasLoadedOnceRef.current = true;
        } catch (error) {
            console.error('[useConversationInfiniteScroll] Erro ao carregar conversas:', error);
        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, [organizationId, connectionId, enabled, pageSize]);

    const { refresh } = useConversationRefresh({
        organizationId,
        connectionId,
        enabled,
        pageSize,
        isLoadingRef,
        hasLoadedOnceRef,
        onConversations: setConversations,
        onInitialLoad: loadInitial,
    });

    const loadMore = useCallback(async () => {
        if (!enabled || !organizationId || !hasMoreConversations || isLoadingRef.current) return;

        const now = Date.now();
        if (now - lastLoadTimeRef.current < 500) return;

        isLoadingRef.current = true;
        lastLoadTimeRef.current = now;
        setIsLoadingMore(true);

        try {
            const result = await chatRepository.getConversationsPaginated(
                organizationId,
                cursorRef.current,
                pageSize,
                connectionId
            );

            if (result.conversations.length > 0) {
                setConversations((prev) => {
                    const existingKeys = new Set(prev.map((c) => conversationDedupeKey(c)));
                    const newConvs = result.conversations.filter((c) => !existingKeys.has(conversationDedupeKey(c)));
                    return [...prev, ...newConvs];
                });
            }

            setHasMoreConversations(result.hasMore);
            cursorRef.current = result.nextCursor;
        } catch (error) {
            console.error('[useConversationInfiniteScroll] Erro ao carregar mais:', error);
        } finally {
            setIsLoadingMore(false);
            isLoadingRef.current = false;
        }
    }, [organizationId, connectionId, enabled, hasMoreConversations, pageSize]);

    const prependConversation = useCallback((conv: ChatConversation) => {
        setConversations(prev => [conv, ...prev.filter(c => c.id !== conv.id)]);
    }, []);

    const updateConversation = useCallback((conv: ChatConversation) => {
        setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, ...conv } : c));
    }, []);

    const reset = useCallback(() => {
        setConversations([]);
        setHasMoreConversations(true);
        cursorRef.current = undefined;
        isLoadingRef.current = false;
        lastLoadTimeRef.current = 0;
        hasLoadedOnceRef.current = false;
    }, []);

    useEffect(() => {
        const orgChanged = previousOrgRef.current !== undefined
            && previousOrgRef.current !== organizationId;
        // null ↔ canal (WA/IG) também precisa limpar — senão merge mistura as duas conexões
        const connChanged = previousConnRef.current !== connectionId
            && (previousConnRef.current !== undefined || connectionId !== undefined);

        if (orgChanged || connChanged) {
            setConversations([]);
            setHasMoreConversations(true);
            cursorRef.current = undefined;
            isLoadingRef.current = false;
            lastLoadTimeRef.current = 0;
            hasLoadedOnceRef.current = false;
        }
        previousOrgRef.current = organizationId;
        previousConnRef.current = connectionId;
    }, [organizationId, connectionId]);

    useEffect(() => {
        if (!enabled || !organizationId) return;
        const timeout = setTimeout(() => {
            if (!hasLoadedOnceRef.current && !isLoadingRef.current) {
                loadInitial();
            }
        }, 1000);
        return () => clearTimeout(timeout);
    }, [enabled, organizationId, loadInitial]);

    return {
        conversations,
        setConversations,
        isLoading,
        isLoadingMore,
        hasMoreConversations,
        loadInitial,
        refresh,
        loadMore,
        prependConversation,
        updateConversation,
        reset,
    };
}
